import { useState } from 'react';
import { LangfuseWeb } from "langfuse";
import { MessageCircle, Star } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { cn } from "@/lib/utils";

interface MessageFeedbackDialogProps {
  traceId: string;
  messageId?: string;
  className?: string;
}

const feedbackOptions = [
  { value: "helpful", label: "Helpful & Accurate", score: 1.0 },
  { value: "mostly_helpful", label: "Mostly Helpful", score: 0.75 },
  { value: "partially_helpful", label: "Partially Helpful", score: 0.5 },
  { value: "not_helpful", label: "Not Helpful", score: 0.25 },
  { value: "problematic", label: "Incorrect/Problematic", score: 0 },
];

export function MessageFeedbackDialog({ traceId, messageId, className }: MessageFeedbackDialogProps) {
  const [open, setOpen] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState<string>("");
  const [additionalComments, setAdditionalComments] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  // Initialize LangfuseWeb client
  const langfuseWeb = new LangfuseWeb({
    publicKey: process.env.NEXT_PUBLIC_LANGFUSE_PUBLIC_KEY,
    baseUrl: process.env.NEXT_PUBLIC_LANGFUSE_BASE_URL || "https://cloud.langfuse.com"
  });

  const handleSubmit = async () => {
    if (!selectedFeedback || isSubmitting) return;
    
    setIsSubmitting(true);
    
    try {
      const selectedOption = feedbackOptions.find(opt => opt.value === selectedFeedback);
      const score = selectedOption?.score ?? 0;
      
      await langfuseWeb.score({
        traceId: traceId,
        name: "user-feedback",
        value: score,
        comment: `${selectedOption?.label}${additionalComments ? ` - ${additionalComments}` : ''}${messageId ? ` (Message ID: ${messageId})` : ''}`
      });
      
      console.log(`[MessageFeedbackDialog] Detailed feedback submitted: ${score} for trace ${traceId}`);
      setHasSubmitted(true);
      
      // Close dialog after a short delay
      setTimeout(() => {
        setOpen(false);
        // Reset state after dialog is closed
        setTimeout(() => {
          setHasSubmitted(false);
          setSelectedFeedback("");
          setAdditionalComments("");
        }, 300);
      }, 1500);
    } catch (error) {
      console.error('[MessageFeedbackDialog] Error submitting feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          className={cn(
            "inline-flex items-center justify-center gap-1 whitespace-nowrap rounded-md",
            "h-6 px-2 text-xs font-medium",
            "ring-offset-background transition-all duration-200",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
            "disabled:pointer-events-none disabled:opacity-50",
            "text-muted-foreground hover:bg-accent hover:text-foreground",
            className
          )}
          onClick={() => setOpen(true)}
        >
          <MessageCircle className="h-3.5 w-3.5" />
          Feedback
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Rate this response</DialogTitle>
          <DialogDescription>
            Help us improve by providing feedback on this assistant's response.
          </DialogDescription>
        </DialogHeader>
        
        {!hasSubmitted ? (
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>How helpful was this response?</Label>
              <RadioGroup value={selectedFeedback} onValueChange={setSelectedFeedback}>
                {feedbackOptions.map((option) => (
                  <div key={option.value} className="flex items-center space-x-2">
                    <RadioGroupItem value={option.value} id={option.value} />
                    <Label 
                      htmlFor={option.value} 
                      className="font-normal cursor-pointer flex-1"
                    >
                      {option.label}
                    </Label>
                    <div className="flex gap-0.5">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={cn(
                            "h-3 w-3",
                            i < Math.round(option.score * 5)
                              ? "fill-yellow-400 text-yellow-400"
                              : "fill-gray-200 text-gray-200"
                          )}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </RadioGroup>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="comments">Additional comments (optional)</Label>
              <Textarea
                id="comments"
                placeholder="Share any specific feedback..."
                value={additionalComments}
                onChange={(e) => setAdditionalComments(e.target.value)}
                rows={3}
              />
            </div>
            
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setOpen(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!selectedFeedback || isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Submit Feedback"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="py-8 text-center">
            <div className="mx-auto w-12 h-12 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mb-4">
              <Star className="h-6 w-6 text-green-600 dark:text-green-400 fill-current" />
            </div>
            <p className="text-lg font-medium mb-2">Thank you for your feedback!</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Your input helps us improve our assistant.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}